import { useMutation, useQuery } from "@tanstack/react-query"
import type { AxiosRequestConfig } from "axios"
import apiClient from "./apiClient"

export type BillingPlanId = "starter" | "pro"
export type BillingInterval = "month" | "year"

export const BILLING_PLAN_IDS: readonly BillingPlanId[] = ["starter", "pro"]

export interface BillingPlanPrice {
  plan: BillingPlanId
  interval: BillingInterval
  /** In the currency's minor unit (cents). */
  unitAmount: number
  currency: string
}

// API response after automatic snake_case -> camelCase conversion by apiClient
export interface BillingStatus {
  checkoutEnabled: boolean
  publishableKey: string | null
  hasActiveSubscription: boolean
  /** The plan of the user's own subscription; null without one. */
  plan: BillingPlanId | null
  canStepUp: boolean
  /** The organization's plan is on a teammate's subscription. */
  paidByTeammate: boolean
  canManageBilling: boolean
  plans: BillingPlanPrice[]
}

export interface CheckoutSession {
  clientSecret: string
  sessionId: string
}

export interface CheckoutSessionStatus {
  status: "open" | "complete" | "expired"
  paymentStatus: string
  /** From the session metadata; null when unknown. */
  plan: string | null
  interval: string | null
}

export interface CheckoutRequest {
  plan: BillingPlanId
  interval: BillingInterval
}

export const BILLING_STATUS_QUERY_KEY = ["billingStatus"] as const

const billingApiCalls = {
  getStatus: async (): Promise<BillingStatus> => {
    // Billing being down must not toast on every page: callers show no error
    const response = await apiClient.get<BillingStatus>("/billing/status", {
      skipErrorToast: true,
    } as AxiosRequestConfig & { skipErrorToast?: boolean })
    return response.data
  },
  createCheckoutSession: async (
    request: CheckoutRequest,
  ): Promise<CheckoutSession> => {
    // The upgrade page shows every failure itself (support contact, retry): no second, generic toast
    const response = await apiClient.post<CheckoutSession>(
      "/billing/checkout-session",
      request,
      { skipErrorToast: true } as AxiosRequestConfig & {
        skipErrorToast?: boolean
      },
    )
    return response.data
  },
  getCheckoutSessionStatus: async (
    sessionId: string,
  ): Promise<CheckoutSessionStatus> => {
    const response = await apiClient.get<CheckoutSessionStatus>(
      `/billing/checkout-session/${encodeURIComponent(sessionId)}`,
    )
    return response.data
  },
  createStepUpSession: async (): Promise<{ url: string }> => {
    const response = await apiClient.post<{ url: string }>("/billing/step-up")
    return response.data
  },
  createPortalSession: async (): Promise<{ url: string }> => {
    const response = await apiClient.post<{ url: string }>(
      "/billing/portal-session",
    )
    return response.data
  },
}

export const getBillingStatus = billingApiCalls.getStatus
export const createCheckoutSession = billingApiCalls.createCheckoutSession
export const getCheckoutSessionStatus = billingApiCalls.getCheckoutSessionStatus
export const createPortalSession = billingApiCalls.createPortalSession

type BillingErrorDetail = { error_code?: unknown; session_id?: unknown }

const errorDetail = (error: unknown): BillingErrorDetail | undefined =>
  (error as { response?: { data?: { detail?: BillingErrorDetail } } })?.response
    ?.data?.detail

/** The `detail.error_code` of a failed billing call (error bodies are not
 *  case-converted), e.g. ALREADY_SUBSCRIBED, PAYMENT_IN_PROGRESS. */
export function billingErrorCode(error: unknown): string | undefined {
  const code = errorDetail(error)?.error_code
  return typeof code === "string" ? code : undefined
}

/** The paid session a PAYMENT_IN_PROGRESS answer names. */
export function billingErrorSessionId(error: unknown): string | undefined {
  const sessionId = errorDetail(error)?.session_id
  return typeof sessionId === "string" ? sessionId : undefined
}

/** `enabled: false` only reads what the app-level listener already loaded (no
 *  request), so entry points work without a QueryClient fetch of their own. */
export const useBillingStatus = (enabled: boolean = true) =>
  useQuery({
    queryKey: BILLING_STATUS_QUERY_KEY,
    queryFn: billingApiCalls.getStatus,
    enabled,
    staleTime: 60 * 1000,
    retry: 1,
  })

export const useCheckoutSessionStatus = (sessionId: string | null) =>
  useQuery({
    queryKey: ["billingCheckoutSession", sessionId],
    queryFn: () => billingApiCalls.getCheckoutSessionStatus(sessionId ?? ""),
    enabled: Boolean(sessionId),
    retry: false,
  })

export const useCreateStepUpSession = () =>
  useMutation({ mutationFn: billingApiCalls.createStepUpSession })

export const useCreatePortalSession = () =>
  useMutation({ mutationFn: billingApiCalls.createPortalSession })
